import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import api from '@/lib/api';

interface SlotData {
  id: string;
  caretaker_id: string;
  date: string;
  start_time: string;
  end_time: string;
  clock_in_time: string | null;
  clock_out_time: string | null;
  status: string;
  caretaker?: {
    id: string;
    first_name: string;
    last_name: string | null;
  };
}

interface ConflictData {
  conflict: true;
  slot: SlotData;
  currentCaretaker: { first_name: string; last_name: string | null } | null;
}

function getTodayPacific(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
}

function formatElapsed(clockInTime: string): string {
  const start = new Date(clockInTime).getTime();
  const now = Date.now();
  const diffMin = Math.floor((now - start) / 60000);
  const hours = Math.floor(diffMin / 60);
  const minutes = diffMin % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function ClockInFAB() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [conflict, setConflict] = useState<ConflictData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState('');

  // Only for caretaker/family roles
  const canClockIn = user?.role === 'caretaker' || user?.role === 'family';

  const today = getTodayPacific();

  // Poll for today's slots to derive active slot
  const { data: todaySlots = [] } = useQuery({
    queryKey: ['timeslots', today, user?.id],
    queryFn: async () => {
      const res = await api.get('/timeslots', {
        params: { start_date: today, end_date: today, include_caretaker: 'true' },
      });
      return (res.data.slots || []) as SlotData[];
    },
    enabled: canClockIn,
    refetchInterval: 10000,
  });

  // Find active slot (clocked in, not clocked out, belongs to user)
  const activeSlot = todaySlots.find(
    (s) => s.caretaker_id === user?.id && s.clock_in_time && !s.clock_out_time && s.status !== 'cancelled'
  );

  // Update elapsed timer
  useEffect(() => {
    if (!activeSlot?.clock_in_time) {
      setElapsed('');
      return;
    }
    setElapsed(formatElapsed(activeSlot.clock_in_time));
    const interval = setInterval(() => {
      setElapsed(formatElapsed(activeSlot.clock_in_time!));
    }, 30000);
    return () => clearInterval(interval);
  }, [activeSlot?.clock_in_time]);

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['timeslots'] });
    queryClient.invalidateQueries({ queryKey: ['my-slots'] });
  }, [queryClient]);

  // Use fetch directly to handle 409 conflict responses properly
  const handleClockIn = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('kaiwatch_token');
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const runAs = sessionStorage.getItem('kaiwatch_runas');
      if (runAs) headers['X-RunAs'] = runAs;

      const res = await fetch(`${baseUrl}/timeslots/quick-clock-in`, {
        method: 'POST',
        headers,
      });

      const data = await res.json();

      if (res.ok) {
        invalidateQueries();
      } else if (res.status === 409 && data.conflict) {
        setConflict(data as ConflictData);
      } else if (res.status === 400 && data.error?.includes('Already clocked in')) {
        invalidateQueries();
      } else {
        setError(data.error || data.message || 'Failed to clock in');
        setTimeout(() => setError(null), 3000);
      }
    } catch (err: any) {
      setError('Network error');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleOverride = async () => {
    if (!conflict) return;
    setLoading(true);
    try {
      await api.post('/timeslots/quick-clock-in/override', { slot_id: conflict.slot.id });
      setConflict(null);
      invalidateQueries();
    } catch (err: any) {
      setError(err.message || 'Failed to override');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.post('/timeslots/quick-clock-out');
      invalidateQueries();
    } catch (err: any) {
      setError(err.message || 'Failed to clock out');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  if (!canClockIn) return null;

  return (
    <>
      {/* Floating Action Button */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
        {/* Error toast */}
        {error && (
          <div className="bg-red-600 text-white text-sm px-4 py-2 rounded-lg shadow-lg max-w-[250px]">
            {error}
          </div>
        )}

        {activeSlot ? (
          /* Clocked In State — Clock Out button */
          <button
            onClick={handleClockOut}
            disabled={loading}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white rounded-full px-5 py-3 shadow-lg transition-all disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
            </svg>
            <span className="font-semibold text-sm">
              {loading ? 'Clocking out...' : `Clock Out${elapsed ? ` (${elapsed})` : ''}`}
            </span>
          </button>
        ) : (
          /* Idle State — Clock In button */
          <button
            onClick={handleClockIn}
            disabled={loading}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-5 py-3 shadow-lg transition-all disabled:opacity-50 animate-pulse hover:animate-none"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-semibold text-sm">
              {loading ? 'Clocking in...' : 'Clock In'}
            </span>
          </button>
        )}
      </div>

      {/* Conflict Confirmation Dialog */}
      {conflict && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-slate-800 border border-slate-600 rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
            <h3 className="text-white font-semibold text-lg mb-2">Shift Conflict</h3>
            <p className="text-gray-300 text-sm mb-4">
              {conflict.currentCaretaker?.first_name || 'Someone'} is scheduled for{' '}
              {conflict.slot.start_time} - {conflict.slot.end_time}. Take over this shift?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConflict(null)}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleOverride}
                disabled={loading}
                className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? 'Confirming...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ClockInFAB;
